import { csShiftTypeNames } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/constants';
import { CSShiftType } from '@/api/apis/resources/schedule';

type CSDeductionRuleColumnKey =
  | 'absenceDays'
  | 'onCall'
  | 'weekendOnCall'
  | 'atHub'
  | 'officeDay'
  | 'flexDays'
  | 'noCall'
  | 'weekendOff'
  | 'offDays'
  | 'daysPresent'
  | 'total';

type CSDeductionRuleColumn = {
  key: CSDeductionRuleColumnKey;
  label: string;
  tooltip: string;
  // Marks columns summed into the Total column (per TMDXLP-13787 AC).
  dotColor?: string;
};

export const csDeductionRuleColumns: CSDeductionRuleColumn[] = [
  {
    key: 'absenceDays',
    label: 'Absence Days',
    tooltip: `Number of absence days (PTO / ${csShiftTypeNames[CSShiftType.Unavailable]} / ${csShiftTypeNames[CSShiftType.Sick]}) in the period. This row drives the calculation: each absence-day count determines how mandatory shifts are reduced.`,
  },
  {
    key: 'onCall',
    label: csShiftTypeNames[CSShiftType.OnCall],
    tooltip: 'Days the CS is On-Call in the period, set as an exact number.',
    dotColor: 'bg-success-40',
  },
  {
    key: 'weekendOnCall',
    label: 'Wknd on Call',
    tooltip: 'How many On-Call shifts the CS may work on weekends; the algorithm assigns between 0 and this value.',
  },
  {
    key: 'atHub',
    label: csShiftTypeNames[CSShiftType.AtHub],
    tooltip: `Maximum ${csShiftTypeNames[CSShiftType.AtHub]} days per period; some may be replaced by ${csShiftTypeNames[CSShiftType.OfficeDay]}, so the algorithm assigns between 0 and this value.`,
    dotColor: 'bg-info-60',
  },
  {
    key: 'officeDay',
    label: csShiftTypeNames[CSShiftType.OfficeDay],
    tooltip: `Minimum ${csShiftTypeNames[CSShiftType.OfficeDay]} shifts per period; the algorithm assigns between this value and the Flex Days total.`,
    dotColor: 'bg-base-50',
  },
  {
    key: 'flexDays',
    label: 'Flex Days',
    tooltip: `The total is fixed each period, but the split can vary: ${csShiftTypeNames[CSShiftType.AtHub]} up to its maximum, ${csShiftTypeNames[CSShiftType.OfficeDay]} at least its minimum (capped by the total). The schedule always plans the same number of Flex Days: only the ${csShiftTypeNames[CSShiftType.AtHub]} / ${csShiftTypeNames[CSShiftType.OfficeDay]} mix may differ.`,
  },
  {
    key: 'noCall',
    label: csShiftTypeNames[CSShiftType.NoCall],
    tooltip: `Minimum ${csShiftTypeNames[CSShiftType.NoCall]} shifts per period; the algorithm assigns between this value and the Off Days total.`,
    dotColor: 'bg-base-50',
  },
  {
    key: 'weekendOff',
    label: csShiftTypeNames[CSShiftType.WeekendOff],
    tooltip: `Maximum ${csShiftTypeNames[CSShiftType.WeekendOff]} shifts per period, placed only on Saturday/Sunday; some may be replaced by ${csShiftTypeNames[CSShiftType.NoCall]}, so the algorithm assigns between 0 and this value.`,
    dotColor: 'bg-base-50',
  },
  {
    key: 'offDays',
    label: 'Day Off',
    tooltip: `The total is fixed each period, but the split can vary: ${csShiftTypeNames[CSShiftType.WeekendOff]} up to its maximum, ${csShiftTypeNames[CSShiftType.NoCall]} at least its minimum (capped by the total). The schedule always plans the same number of Off Days: only the ${csShiftTypeNames[CSShiftType.WeekendOff]} / ${csShiftTypeNames[CSShiftType.NoCall]} mix may differ.`,
  },
  {
    key: 'daysPresent',
    label: 'Days Present',
    tooltip: 'Days the CS is present in the period = 28 − Absence Days.',
  },
  {
    key: 'total',
    label: 'Total',
    tooltip: `The full period must always total 28 days. Total = Days Present + Absence Days = 28, which is the same as ${csShiftTypeNames[CSShiftType.OnCall]} + ${csShiftTypeNames[CSShiftType.AtHub]} + ${csShiftTypeNames[CSShiftType.OfficeDay]} + ${csShiftTypeNames[CSShiftType.NoCall]} + ${csShiftTypeNames[CSShiftType.WeekendOff]} + Absence Days = 28. Must equal 28 for the table to be saved.`,
  },
];
